import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hərrac timer hook - düzəldilmiş versiya
 * @param {string|Date|null} endDate - Hərracın bitmə tarixi
 * @param {Object} options - Callback funksiyalar
 * @returns {Object} Timer state
 */
export function useAuctionTimer(endDate, options = {}) {
    const { onEnd, onWarning } = options;
    
    // Refs - dövrəyə düşməyi önləmək üçün
    const intervalRef = useRef(null);
    const hasEndedRef = useRef(false);
    const hasWarnedRef = useRef(false);
    const onEndRef = useRef(onEnd);
    const onWarningRef = useRef(onWarning);
    
    // Refs-i yenilə (callback dəyişdikdə)
    useEffect(() => {
        onEndRef.current = onEnd;
        onWarningRef.current = onWarning;
    }, [onEnd, onWarning]);

    // Initial state
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        total: -1,
        isEnded: false,
        isLoading: true,
        isWarning: false,
        isUrgent: false
    });

    // Vaxt hesablama funksiyası - stabil
    const calculateTimeLeft = useCallback(() => {
        // EndDate yoxdursa - loading state
        if (!endDate) {
            return {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                total: -1,
                isEnded: false,
                isLoading: true,
                isWarning: false,
                isUrgent: false
            };
        }

        const end = new Date(endDate).getTime();
        
        // Invalid date
        if (isNaN(end)) {
            return {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                total: -1,
                isEnded: false,
                isLoading: true,
                isWarning: false,
                isUrgent: false
            };
        }

        const now = Date.now();
        const difference = end - now;

        // Vaxt bitib
        if (difference <= 0) {
            return {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                total: 0,
                isEnded: true,
                isLoading: false,
                isWarning: false,
                isUrgent: false
            };
        }

        // Vaxt hesabla
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        // 5 dəqiqədən az qalıb
        const isWarning = difference <= 5 * 60 * 1000;
        // 1 dəqiqədən az qalıb
        const isUrgent = difference <= 60 * 1000;

        return {
            days,
            hours,
            minutes,
            seconds,
            total: difference,
            isEnded: false,
            isLoading: false,
            isWarning,
            isUrgent
        };
    }, [endDate]);

    // Timer effect
    useEffect(() => {
        // Əvvəlki interval-ı təmizlə
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // endDate yoxdursa - loading göstər
        if (!endDate) {
            setTimeLeft({
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                total: -1,
                isEnded: false,
                isLoading: true,
                isWarning: false,
                isUrgent: false
            });
            return;
        }

        // Invalid date check
        const end = new Date(endDate).getTime();
        if (isNaN(end)) {
            setTimeLeft({
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                total: -1,
                isEnded: false,
                isLoading: true,
                isWarning: false,
                isUrgent: false
            });
            return;
        }

        // Flags sıfırla (yeni endDate gəldikdə)
        hasEndedRef.current = false;
        hasWarnedRef.current = false;

        // İlkin hesablama
        const initialTime = calculateTimeLeft();
        setTimeLeft(initialTime);

        // Əgər artıq bitibsə, interval qurma
        if (initialTime.isEnded) {
            if (!hasEndedRef.current && onEndRef.current) {
                hasEndedRef.current = true;
                onEndRef.current();
            }
            return;
        }

        // Timer interval
        intervalRef.current = setInterval(() => {
            const newTime = calculateTimeLeft();
            
            setTimeLeft(prev => {
                // Əgər dəyişiklik yoxdursa, yenilənməsin
                if (
                    prev.days === newTime.days &&
                    prev.hours === newTime.hours &&
                    prev.minutes === newTime.minutes &&
                    prev.seconds === newTime.seconds &&
                    prev.isEnded === newTime.isEnded &&
                    prev.isLoading === newTime.isLoading
                ) {
                    return prev;
                }
                return newTime;
            });

            // Bitdikdə callback (bir dəfə)
            if (newTime.isEnded && !hasEndedRef.current) {
                hasEndedRef.current = true;
                if (onEndRef.current) {
                    onEndRef.current();
                }
                // Interval-ı dayandır
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }

            // Warning callback (bir dəfə)
            if (newTime.isWarning && !hasWarnedRef.current && !newTime.isEnded) {
                hasWarnedRef.current = true;
                if (onWarningRef.current) {
                    onWarningRef.current();
                }
            }
        }, 1000);

        // Cleanup
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [endDate, calculateTimeLeft]);

    return timeLeft;
}

export default useAuctionTimer;